import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {FeatureFlagService, selectActiveVehicle} from '@ox/data';
import {FtcAcceptanceMap, FtcContent, UIFeatureFlagsEnum} from '@ox/typings';
import {ApiCacheService, AutoDestroy, BML, getBaseHeaders, SessionStorageService} from '@ox/utils';
import {Subject, switchMap, take, catchError, map, of, tap, combineLatest} from 'rxjs';
import {DomSanitizer} from '@angular/platform-browser';
import {OX_KEYS} from '@ox/config';

@Injectable()
export class EcppDataService {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  private activeVehicle$ = this.store.select(selectActiveVehicle);
  private ecppAccountServiceUrl = '/oc-account-service/cust/legal';
  private bml = inject(BML);

  private ftcFlag$ = this.featureFlagService.featureFlags$
  .pipe(
    take(1),
    map(ff => ff.UI[UIFeatureFlagsEnum.OC_FTC_ENABLED]));

  constructor(
    private store: Store,
    private http: HttpClient,
    private apiCache: ApiCacheService,
    private sanitizer: DomSanitizer,
    private session: SessionStorageService,
    private featureFlagService: FeatureFlagService
  ) {}

  // GET Request to fetch FTC User Acceptance Data
  getFTCAcceptanceData = (forceFetch = false) => combineLatest([this.activeVehicle$, this.ftcFlag$]).pipe(
    take(1),
    switchMap(([vehicle, ftcFlag]) => {

      // If FTC feature flag is not enabled, do not fetch data - CANADA
      if(!ftcFlag) {
        return of({} as FtcAcceptanceMap);
      }

      if (!vehicle?.vin) {
        throw new Error('Invalid Vehicle');
      }

      // GET Hash data and decrypt it if available in session storage
      const ftcHashData = this.session.getFromJson<string>(OX_KEYS.FTC_ACCEPTANCE);
      const ftcSessionData: FtcAcceptanceMap = ftcHashData ? JSON.parse(atob(ftcHashData)) : {};

      if(!forceFetch && ftcSessionData && ftcSessionData[vehicle?.vin] !== undefined) {
        return of(ftcSessionData);
      }

      return this.apiCache.get<boolean>({
        tokenOrUrl: `${this.ecppAccountServiceUrl}/ftc`,
        headers: getBaseHeaders(this.bml).concat([
          {vin: vehicle.vin}
        ])
      }).pipe(
        take(1),
        map((response) => {
          if(!response) throw new Error('Fetching FTC User Acceptance Failed');
          ftcSessionData[vehicle.vin] = response.data;
          this.session.setAsJson(OX_KEYS.FTC_ACCEPTANCE, btoa(JSON.stringify(ftcSessionData)));
          return ftcSessionData;
        }),
        catchError((error) => {throw new Error(error);})
      );
    })
  );

  // GET Request to fetch FTC Content - HTML and PDF url path
  getFTCContent() {
    const headers = getBaseHeaders(this.bml);

    return this.apiCache.get<FtcContent>({
      tokenOrUrl: `${this.ecppAccountServiceUrl}/ftc/terms`,
      headers
    }).pipe(
      take(1),
      map(({data}) => {
        if (data) return data;
        throw new Error('Fetching FTC Content Failed');
      }),
      catchError((error) => {throw error;})
    );
  }

  // Request to fetch HTML data to render on UI
  getHtmlData(html: string) {
    return this.http.get(
      html,
      {responseType: 'text'}
    ).pipe(
      take(1),
      map(response => this.sanitizer.bypassSecurityTrustHtml(response))
    );
  }

  // POST Request to submit FTC Acceptance
  submitFTCAcceptance = () => this.activeVehicle$
    .pipe(
      take(1),
      switchMap((vehicle) => {
        if (!vehicle?.vin) {
          throw new Error('Invalid Vehicle');
        }

        const headers = getBaseHeaders(this.bml).concat([
          {vin: vehicle.vin}
        ]);

        return this.apiCache.post<boolean>({
          tokenOrUrl: `${this.ecppAccountServiceUrl}/ftc`,
          headers
        }).pipe(
          take(1),
          tap(response => {
            if (!response) throw new Error('Submit FTC Acceptance Failed');
          }),
          map(({data}) => {
            if (data) return data;
            throw new Error('Submit FTC Acceptance Failed');
          }),
          catchError((error) => {throw error;})
        );
      }),
      catchError((error) => {
        throw error;
      })
    );
}
